import React, { createContext, useState, useEffect, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useNotif } from 'src/hooks';
import { useSelector, useDispatch } from 'react-redux';
import { setStatus } from 'src/redux/slices/wf';
import { logout } from 'src/redux/slices/auth';
import { useNavigate } from 'react-router';
import { defaultNotifOptions } from 'src/constants';
// Create the context
const SocketContext = createContext(null);
const SOCKETS = ['connect', 'message', 'command', 'error', 'disconnect'];

//settings
const socketURL = window.location.origin;

// Create the provider
const SocketProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { success, info, error, custom } = useNotif();
  const [currentBiz, setCurrentBiz] = useState(null);
  const { userToken, current_user, ixBiz } = useSelector(
    state => state.socketio
  );
  const basicInfo = {
    username: current_user?.username,
    room: 'general'
  };
  const [socket, setSocket] = useState(null);
  const { frontEnd } = useSelector(({ bizMeta }) => bizMeta);
  const isSocketAllowed = frontEnd?.socket?.allow ?? false;
  const storeNotifications = frontEnd?.socket?.store_notifications ?? false;
  const specificUserNotif = frontEnd?.socket?.specific_user_notif ?? true;
  //socket options
  const extraHeaders = {
    Authorization: userToken,
    username: current_user?.username ?? null
  };
  const query = { token: userToken, username: current_user?.username ?? null };

  //functions for commands
  const COMMANDLOGIC = {
    WF_Update: data => {
      const {
        message,
        linkText,
        path,
        timestamp,
        jid,
        wfStatus,
        jStatus,
        username,
        assignedTo
      } = data;

      dispatch(
        setStatus({
          jid: jid,
          wfStatus: wfStatus,
          jStatus: jStatus
        })
      );
      const isRelevant =
        !specificUserNotif ||
        (username !== current_user.username &&
          (Array.isArray(assignedTo) ? assignedTo : []).includes(
            current_user.username
          ));

      if (!isRelevant) return;

      custom({
        text: message,
        variant: 'info',
        linkText: linkText,
        path: path,
        notifKey: timestamp,
        options: defaultNotifOptions
      });
    },
    Batch_WF_Update: data => {
      // Off for now, can't specify
      // if (data.username !== current_user.username) {
      //   custom({
      //     text: data.message,
      //     variant: 'info',
      //     options: defaultNotifOptions
      //   });
      // }

      // Apply updates for each jid
      data.jid.forEach(jid => {
        dispatch(
          setStatus({ jid, wfStatus: data.wfStatus, jStatus: data.jStatus })
        );
      });
    },
    JStatus_Update: data => {
      dispatch(
        setStatus({
          jid: data.jid,
          wfStatus: data.wfStatus,
          jStatus: data.jStatus
        })
      );
    },
    Assigned_Update: data => {
      if (
        data.userList.includes(current_user.username) &&
        data.username != current_user.username
      ) {
        const { message, linkText, path, timestamp } = data;

        custom({
          text: message,
          variant: 'info',
          linkText: linkText,
          path: path,
          notifKey: timestamp,
          options: defaultNotifOptions
        });
      }
    },
    Logout: data => {
      if (data.targetUser == current_user.username) {
        dispatch(logout());
        navigate('/app/login');
      }
    }
  };
  //functions for listeners
  const LISTENERS = {
    connect: () => {},
    command: data => {
      const timestamp = Date.now();
      data.timestamp = timestamp;
      StoreCommandsReceived(data);
      COMMANDLOGIC[data.type](data);
    },
    message: data => {
      // console.log(data);
    },
    error: data => {
      error(data);
    },
    disconnect: () => {}
  };
  // localstorage
  function StoreCommandsReceived(ReceivedData) {
    const data = {
      jStatus: ReceivedData?.jStatus ?? '',
      message: ReceivedData?.message ?? '',
      path: ReceivedData?.path ?? '',
      linkText: ReceivedData?.linkText ?? ''
    };
    if (!storeNotifications || data?.message == '') {
      return;
    }

    const isAssignedToCurrentUser =
      ReceivedData?.username !== current_user.username &&
      ((Array.isArray(ReceivedData?.assignedTo) &&
        ReceivedData.assignedTo.includes(current_user.username)) ||
        (Array.isArray(ReceivedData?.userList) &&
          ReceivedData.userList.includes(current_user.username)));

    const isAllowedToSave =
      !specificUserNotif && ReceivedData?.type !== 'Assigned_Update';

    if (isAllowedToSave || isAssignedToCurrentUser) {
      const now = ReceivedData?.timestamp ?? Date.now();
      let storedCommandsReceived =
        JSON.parse(localStorage.getItem('CommandsReceived')) || {};

      const lastTimestamp = Math.max(
        ...Object.keys(storedCommandsReceived).map(Number),
        0
      );

      if (now - lastTimestamp < 300) return; // throttle too-frequent saves

      const timestamps = Object.keys(storedCommandsReceived)
        .map(Number)
        .sort((a, b) => a - b);
      if (timestamps.length >= 5) {
        delete storedCommandsReceived[timestamps[0]]; // remove oldest
      }

      storedCommandsReceived[now] = {
        data,
        read: false,
        biz: ixBiz ?? ''
      };

      localStorage.setItem(
        'CommandsReceived',
        JSON.stringify(storedCommandsReceived)
      );
      window.dispatchEvent(new Event('CommandsUpdated'));
    }
  }

  //socket connection initialization
  useEffect(() => {
    if (!isSocketAllowed) {
      return;
    }
    if (!current_user.username && !userToken) {
      if (socket) {
        socket.disconnect(); // 🔹 Disconnect if state becomes invalid
        setSocket(null);
      }
      return;
    }

    if (socket) return;

    const testSocket = io(socketURL, {
      query: query,
      extraHeaders: extraHeaders,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
      // transports: ['websocket'], // force websocket only
      // reconnection: false // no retry
    });

    SOCKETS.forEach(item => {
      testSocket.on(item, data => {
        LISTENERS[item](data);
      });
    });

    setSocket(testSocket);

    if (ixBiz && current_user && isSocketAllowed) {
      if (currentBiz === null) {
        setCurrentBiz(ixBiz);
      }
      if (currentBiz != ixBiz) {
        leaveRoom(currentBiz);
        setCurrentBiz(ixBiz);
      }
      testSocket.emit('join_room', {
        ixBiz: ixBiz,
        room: 'general',
        username: current_user.username
      });
    }

    return () => {
      if (testSocket) {
        testSocket.disconnect();
        SOCKETS.forEach(item => {
          testSocket.off(item);
        });
      }
    };
  }, [userToken, isSocketAllowed, current_user]);

  // Function to join a room
  const joinRoom = (biz, username) => {
    if (socket) {
      if (biz && username && isSocketAllowed) {
        socket.emit('join_room', {
          ixBiz: ixBiz,
          room: 'general',
          username: username
        });
      }
    }
  };

  // Function to leave a room
  const leaveRoom = room => {
    if (room && socket) {
      socket.emit('leave_room', { room });
    }
  };

  // Functions to send command/data
  // data = object
  // required ixBiz, username, room
  const sendMessage = data => {
    if (socket) {
      const mergedData = { ixBiz, ...basicInfo, ...data };
      socket.emit('send_data', mergedData);
    }
  };
  const sendCommand = data => {
    if (socket) {
      const mergedData = { ixBiz, ...basicInfo, ...data };
      socket.emit('send_command', mergedData);
    }
  };

  function Disconnect() {
    if (socket) {
      socket.disconnect();
    }
  }
  function RemoteLogout() {
    if (socket) {
      const data = {
        type: 'Logout',
        targetUser: current_user.username
      };
      const mergedData = { ixBiz, ...basicInfo, ...data };
      socket.emit('send_command', mergedData);
    }
  }

  return (
    <SocketContext.Provider
      value={{
        socket,
        joinRoom,
        leaveRoom,
        sendMessage,
        sendCommand,
        Disconnect,
        RemoteLogout
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
export { SocketProvider };
export default SocketContext;
